import { createWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { persist, createJSONStorage } from "zustand/middleware";
import i18n from "i18next";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { useMemberStore } from "@companion-core/shared/app/Store/memberStore";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";
import { useAccountNavigationStore } from "@companion-core/shared/app/Store/accountNavigationStore";
import { useDynamicPageStepperStore } from "@companion-core/shared/app/Store/dynamicPageStepperStore";
import {
  authLogin,
  authLogout,
  reFetchAllResources,
} from "@companion-core/shared/app/Utils/apiClient";
import { isWeb } from "@companion-core/shared/app/Utils/platform";
import { clearQueryCache } from "@companion-core/shared/app/Utils/queryClient";
import { setUser } from "@companion-core/shared/app/tracking";

interface AuthType {
  success: boolean;
  hash?: string;
  message?: string;
}

export interface SSOOptions {
  client_id: string;
  response_type: string;
  redirect_uri: string;
  state: string;
  scope: string;
  code_challenge: string;
  code_challenge_method: string;
}

interface AuthState {
  accessTokenClient: string | null;
  authenticated: boolean;
  error: string | null;
  revokeError: string | null;
  loading: boolean;
  loggingOut: boolean;
  revoking: boolean;
  memberToRegister: Record<string, unknown> | null;
  biometricEnabled?: boolean;
  hasNeverTriedToEnableBiometric?: boolean;
  onboardingHasAppeared?: boolean;
  redirectionAfterLogin?: string;
  isWritingInSecureStorage: boolean;
  isReadingInSecureStorage: boolean;
  isAppDownloadBannerVisible: boolean;
  isRestrictedSession: boolean;
}

interface AuthActions {
  setAccessTokenClient: (accessTokenClient: string | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setMemberToRegister: (memberToRegister: Record<string, unknown> | null) => void;
  setBiometricEnabled: (enable: boolean) => void;
  setHasTriedToEnableBiometric: (reset?: boolean) => void;
  setIsWritingInSecureStorage: (isWriting: boolean) => void;
  setIsReadingInSecureStorage: (isReading: boolean) => void;
  setOnboardingHasAppeared: (onboardingHasAppeared: boolean) => void;
  setRedirectionAfterLogin: (redirectionAfterLogin?: string) => void;
  setIsAppDownloadBannerVisible: (isAppDownloadBannerVisible: boolean) => void;
  setIsRestrictedSession: (isRestrictedSession: boolean) => void;
  authAction: (username: string, password: string, alreadyHashed: boolean) => Promise<AuthType>;
  logout: (withRevoke?: boolean, fetchResources?: boolean) => Promise<void>;
  resetAuth: () => void;
}

export const initialState: AuthState = {
  accessTokenClient: null,
  authenticated: false,
  error: null,
  revokeError: null,
  loading: false,
  loggingOut: false,
  onboardingHasAppeared: false,
  revoking: false,
  memberToRegister: null,
  redirectionAfterLogin: undefined,
  isWritingInSecureStorage: false,
  isReadingInSecureStorage: false,
  isAppDownloadBannerVisible: true,
  isRestrictedSession: false,
};

export const useAuthStore = createWithEqualityFn<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,
      ...{
        biometricEnabled: false,
        hasNeverTriedToEnableBiometric: true,
      },
      setAccessTokenClient: (accessTokenClient: string | null) => set({ accessTokenClient }),
      setAuthenticated: (authenticated: boolean) => set({ authenticated }),
      setError: (error: string | null) => set({ error }),
      setLoading: (loading: boolean) => set({ loading }),
      setMemberToRegister: (memberToRegister: Record<string, unknown> | null) =>
        set({ memberToRegister }),
      setBiometricEnabled: (enabled: boolean) => set({ biometricEnabled: enabled }),
      setOnboardingHasAppeared: (onboardingHasAppeared: boolean) => set({ onboardingHasAppeared }),
      setHasTriedToEnableBiometric: (reset?: boolean) =>
        set({ hasNeverTriedToEnableBiometric: !!reset }),
      setIsWritingInSecureStorage: (isWriting: boolean) =>
        set({ isWritingInSecureStorage: isWriting }),
      setIsReadingInSecureStorage: (isReading: boolean) =>
        set({ isReadingInSecureStorage: isReading }),
      setRedirectionAfterLogin: (redirectionAfterLogin?: string) => set({ redirectionAfterLogin }),
      setIsAppDownloadBannerVisible: (isAppDownloadBannerVisible: boolean) =>
        set({ isAppDownloadBannerVisible }),
      setIsRestrictedSession: (isRestrictedSession: boolean) => set({ isRestrictedSession }),
      authAction: async (username: string, password: string, alreadyHashed: boolean) => {
        try {
          set({ loading: true });
          const { hash } = await authLogin(username, password, alreadyHashed);
          await reFetchAllResources(true);
          set({ loading: false });
          return { success: true, hash };
        } catch (error: any) {
          const { message } = error;
          set({ loading: false, error: i18n.t(`login.error.${message ? message : "default"}`) });
          return { success: false, message };
        }
      },
      logout: async (withRevoke = true, fetchResources = true) => {
        if (!get().revoking) {
          try {
            set({ ...initialState, revoking: true, loggingOut: true });
            setUser("", "", ""); // Reset User infos for tracking events
            // We also need to reset all other stores and the server-state cache
            clearQueryCache();
            useBellStore.getState().resetBell(useBellStore.getState().isIntroPopUpVisible);
            useAccountNavigationStore.getState().resetAccountNavigation();
            useMemberStore.getState().resetMember();
            useWizardStore.getState().resetWizard();
            set({ onboardingHasAppeared: false });
            useDynamicPageStepperStore.getState().resetPage("onboarding");
            if (withRevoke) await authLogout();
            if (fetchResources) await reFetchAllResources(false);
          } catch (error: any) {
            set({ error: error });
          } finally {
            set({ revoking: false, loggingOut: false });
          }
        }
      },
      resetAuth: () => set({ ...initialState }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => (isWeb() ? localStorage : AsyncStorage)),
      partialize: (state) => {
        const stateToPersist = ["authenticated", "isAppDownloadBannerVisible"];
        if (!isWeb()) {
          stateToPersist.push("biometricEnabled", "hasNeverTriedToEnableBiometric");
        }
        return Object.fromEntries(
          Object.entries(state).filter(([key]) => stateToPersist.includes(key))
        );
      },
    }
  ),
  shallow
);
